import type {QueuedLogRecord} from "@apps/qset-log-batcher/policy";

/**
 * `Video errors:` / `Video freeze:` / `Video stalls:` lines of the feedback report (#1249): a
 * one-glance summary of what the debug log already holds for the video on
 * stage, so a reader sees "403 on fragment 5" or "stuck loading fragment 4
 * with a hole ahead" without opening RTDB.
 *
 * Built from the batcher's local buffer, not from hls.js directly: the
 * player lives inside the qset package, and exposing it to the host would
 * cost the entry bundle bytes it does not have. So the lines say only what
 * was logged -- a freeze shows up after 5 s, non-fatal errors of one kind at
 * most every 5 s, and only within the buffer's last 200 events. v3 logs
 * nothing, so there the lines are absent.
 */

type Data = Record<string, unknown>;

const num = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const str = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined;

const seconds = (value: number): string => `${value.toFixed(1)} s`;

const ago = (now: number, timestamp: number): string =>
  `${seconds((now - timestamp) / 1000)} ago`;

const isHlsError = (record: QueuedLogRecord): boolean =>
  record.message === "error" && record.data?.source === "hls";

const describeFragment = (data: Data): string | undefined => {
  const sn = data.fragSn;
  if (sn === undefined) return undefined;
  const type = str(data.fragType);
  const start = num(data.fragStart);
  return [
    `frag ${String(sn)}`,
    type,
    start === undefined ? undefined : `@${seconds(start)}`,
  ]
    .filter(Boolean)
    .join(" ");
};

const describeError = (data: Data): string => {
  const status = num(data.httpStatus);
  const action = str(data.errorAction);
  const retry = num(data.retryCount);
  const buffer = num(data.bufferAhead);
  return [
    str(data.details) ?? "unknown",
    str(data.type),
    status === undefined ? undefined : `HTTP ${status}`,
    describeFragment(data),
    action && (retry === undefined ? action : `${action} #${retry}`),
    buffer === undefined ? undefined : `buffer ${seconds(buffer)}`,
  ]
    .filter(Boolean)
    .join(", ");
};

const describeInFlight = (
  label: string,
  value: unknown,
): string | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const {sn, state} = value as {sn?: unknown; state?: unknown};
  if (typeof state !== "string") return undefined;
  return sn === undefined
    ? `${label} ${state}`
    : `${label} ${state} sn ${String(sn)}`;
};

const describeFreeze = (data: Data): string => {
  const time = num(data.currentTime);
  const readyState = num(data.readyState);
  const ahead = num(data.bufferedAhead);
  const hole = num(data.hlsNextBufferedStart);
  const playlist = num(data.hlsPlaylistDuration);
  const endSn = num(data.hlsPlaylistEndSn);
  const ttfb = num(data.hlsTtfbEstimate);
  return [
    time === undefined ? "last" : `last at ${seconds(time)}`,
    readyState === undefined ? undefined : `readyState ${readyState}`,
    ahead === undefined ? undefined : `buffered ahead ${seconds(ahead)}`,
    describeInFlight("main", data.hlsInFlightMain),
    describeInFlight("audio", data.hlsInFlightAudio),
    hole === undefined ? undefined : `hole to ${seconds(hole)}`,
    playlist === undefined
      ? undefined
      : endSn === undefined
        ? `playlist ${seconds(playlist)}`
        : `playlist ${seconds(playlist)} (last sn ${endSn})`,
    ttfb === undefined ? undefined : `ttfb ${Math.round(ttfb)} ms`,
  ]
    .filter(Boolean)
    .join(", ");
};

export const formatVideoLogLines = (
  records: ReadonlyArray<QueuedLogRecord>,
  questionId: string | null | undefined,
  now: number,
): string | undefined => {
  if (!questionId) return undefined;
  const own = records.filter((r) => r.data?.questionId === questionId);
  // A video widget's `question-enter` counts too: a clip that never got as
  // far as `video-load` is exactly the report these lines are for.
  const isVideo = own.some(
    (r) =>
      r.message.startsWith("video-") ||
      r.message.startsWith("hls-") ||
      isHlsError(r) ||
      (r.message === "question-enter" &&
        String(r.data?.widget ?? "").startsWith("video")),
  );
  if (!isVideo) return undefined;

  const errors = own.filter(isHlsError);
  const lastError = errors[errors.length - 1];
  const fatal = errors.filter((r) => r.data?.fatal === true).length;
  const errorLine = lastError
    ? `Video errors: ${errors.length}${fatal ? ` (${fatal} fatal)` : ""}; last ${describeError(lastError.data ?? {})}, ${ago(now, lastError.timestamp)}`
    : "Video errors: none";

  const freezes = own.filter((r) => r.message === "video-frozen");
  const lastFreeze = freezes[freezes.length - 1];
  const freezeLine = lastFreeze
    ? `Video freeze: ${freezes.length}x; ${describeFreeze(lastFreeze.data ?? {})}, ${ago(now, lastFreeze.timestamp)}`
    : "Video freeze: none";

  const resolved = own
    .filter((r) => r.message === "hls-stall-resolved")
    .map((r) => num(r.data?.stalledMs))
    .filter((ms): ms is number => ms !== undefined);
  const stallLine =
    resolved.length > 0
      ? `Video stalls: ${resolved.length} resolved by hls.js, longest ${seconds(Math.max(...resolved) / 1000)}`
      : "Video stalls: none resolved";

  return `${errorLine}\n${freezeLine}\n${stallLine}`;
};
